import { Injectable, inject, signal } from '@angular/core';
import type { HttpErrorResponse } from '@angular/common/http';
import type { Subscription } from 'rxjs';
import { ApiService } from '../api.service';

/**
 * 서비스 입장(매직링크 핸드오프) — 서버가 노출·자격을 판정한 뒤 user/org 토큰을 발급해 RP 콜백 URL을 돌려준다
 * (`POST /oauth/service-connections/magic-link`). 사이드바 「RP 서비스」와 단체 대시보드의 입장 버튼이 같은 흐름을 쓰도록
 * 한 곳에 둔다. 학생·학부모(team-only)도 쓰므로 단체 관리 영역 가드(`orgAdminAreaGuard`)가 걸리는 라우트 이동이 아니라
 * 호출 후 전체 페이지 이동으로 처리한다.
 *
 * 진행 중인 입장은 하나만 허용한다 — 응답이 오기 전에 다시 누르면 같은 토큰이 두 번 발급될 수 있다. 성공하면 RP로 전체
 * 페이지 이동하므로 `entering`을 내리지 않는다(내리면 이동이 끝나기 전에 버튼이 다시 열린다).
 */
@Injectable({ providedIn: 'root' })
export class ServiceEntryService {
  private readonly api = inject(ApiService);

  /** 입장 요청 중인 서비스의 clientId. 없으면 `null`. */
  readonly entering = signal<string | null>(null);
  /**
   * 화면 안에 문구를 둘 자리가 있는 호출(단체 대시보드의 입장 버튼, `surface: 'inline'`)의 실패 문구. 새 입장을 시작하거나 `reset()`하면 비운다.
   */
  readonly error = signal<string | null>(null);
  /**
   * 문구를 둘 자리가 없는 호출(사이드바, `surface: 'toast'`)의 실패 문구 — 알림으로 한 번 보여 준 뒤 `clearToastError()`로 비운다.
   * `error`와 나눈 이유: 하나를 같이 쓰면 사이드바 실패가 단체 대시보드에도 이중으로 표시되고, 알림을 본 뒤 같은 단체의 대시보드를 열면 낡은
   * 문구가 남는다.
   */
  readonly toastError = signal<string | null>(null);

  private sub?: Subscription;
  /** 응답을 기다리는 동안 먼저 열어 둔 빈 탭 — 실패·취소 시 닫는다. */
  private pendingTab: Window | null = null;

  /**
   * `surface`: 실패 문구를 어디에 보일지 — 화면 안(`inline`, 기본)이면 `error`, 알림(`toast`)이면 `toastError`에 둔다.
   * `teamId`: 있으면 팀 단위 입장이다(팀 뷰 사이드바). 없으면 단체 단위 입장이다.
   * `target`: `'new-tab'`이면 현재 화면을 두고 새 탭에서 RP를 연다. 응답을 기다린 뒤에 `window.open`을 부르면 팝업 차단에 걸리므로
   * 클릭 시점에 빈 탭을 먼저 열어 두고 응답이 오면 그 탭을 이동시킨다. 탭을 열 수 없으면(차단) 같은 탭 이동으로 되돌린다.
   */
  enter(
    orgId: string,
    service: { clientId: string; name: string },
    surface: 'inline' | 'toast' = 'inline',
    teamId?: string,
    target: 'self' | 'new-tab' = 'self',
  ): void {
    if (this.entering() !== null) return;
    const tab = target === 'new-tab' ? this.openBlankTab() : null;
    this.pendingTab = tab;
    this.entering.set(service.clientId);
    this.error.set(null);
    this.toastError.set(null);
    const params = { orgId, oauthClientId: service.clientId, ...(teamId ? { teamId } : {}) };
    this.sub = this.api.enterServiceViaMagicLink(params).subscribe({
      next: (res) => {
        if (tab && !tab.closed) {
          // 현재 화면은 그대로 남으므로 진행 표시를 내려 다른 서비스도 이어서 열 수 있게 한다.
          this.pendingTab = null;
          this.navigateTab(tab, res.redirectTo);
          this.entering.set(null);
        } else if (tab) {
          // 사용자가 빈 탭을 먼저 닫았다 — 입장을 취소한 것으로 본다.
          this.pendingTab = null;
          this.entering.set(null);
        } else {
          this.redirectTo(res.redirectTo);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.closePendingTab();
        const message =
          err.status === 403
            ? `'${service.name}'은(는) 지금 사용할 수 없습니다. 이 단체(팀)에서 사용 중이 아니거나 접근 권한이 없습니다.`
            : `'${service.name}'(으)로 이동하는 중 오류가 발생했습니다. 다시 시도해 주세요.`;
        (surface === 'toast' ? this.toastError : this.error).set(message);
        this.entering.set(null);
      },
    });
  }

  /** 알림으로 보여 준 실패 문구를 비운다 — 같은 문구가 다시 나와도 다음 실패에서 새로 알림이 뜨게 한다. */
  clearToastError(): void {
    this.toastError.set(null);
  }

  /** 단체가 바뀌면 이전 단체 기준으로 진행 중이던 입장을 취소하고 상태를 비운다 — 늦게 도착한 응답이 다른 단체 화면에서 RP로 이동시키는 것을 막는다. */
  reset(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    this.closePendingTab();
    this.entering.set(null);
    this.error.set(null);
    this.toastError.set(null);
  }

  private closePendingTab(): void {
    this.pendingTab?.close();
    this.pendingTab = null;
  }

  // 테스트에서 window 접근을 가로채기 위해 분리했다.
  protected openBlankTab(): Window | null {
    const tab = window.open('', '_blank');
    // noopener 로 열면 반환값이 null 이라 이동시킬 수 없다 — 열린 뒤 opener 만 끊어 RP가 이 창을 참조하지 못하게 한다.
    if (tab) tab.opener = null;
    return tab;
  }

  protected navigateTab(tab: Window, url: string): void {
    tab.location.href = url;
  }

  // 테스트에서 전체 페이지 이동을 가로채기 위해 분리했다(jsdom은 location 대입을 막는다).
  protected redirectTo(url: string): void {
    window.location.href = url;
  }
}
